import { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

import Badge from '../../components/base/Badge';
import Button from '../../components/base/Button';

import { INFLUX_API } from '../../config/api';
import LazyLoadChart from '../../components/common/LazyLoadChart';
import MachinePercentageLineChart from '../../components/charts/machines-charts/MachinePercentageLineChart';
import MachineDoubleLineChart from '../../components/charts/machines-charts/MachineDoubleLineChart';
import MachineTempZonesChart from '../../components/charts/machines-charts/MachineTempZonesChart';
import { chartsFor, SUMMARY_FIELDS, SUMMARY_GATES } from '../../components/project-management/token-modal/tokenModalMachineCharts.js';
import TokenPhaseSpec from '../../components/project-management/token-modal/TokenPhaseSpec.js';

// One drum's machine traces, on their own.
//
// A PAGE, not a modal. It started as a fullscreen modal, which covered the
// navigation and dropped you out of the application — you could not tell where
// you were or get anywhere else without closing it first. A route renders inside
// MainLayout, so the navbar and the breadcrumbs come along.
//
// Everything it needs arrives in the router's location state, put there by the
// token modal that opened it: the drum, its task, the job's phase plan and the
// window. Nothing is re-fetched, so there is no second source to disagree with
// the screen you came from.
//
// Everything here is a real <Card>. A hand-rolled `bg-body` panel is INVISIBLE
// on the dark theme: `--phoenix-soft` and `--phoenix-body-bg` are both #0f111a
// there, so a "card" on the page background had no edge at all. `.dark .card`
// resolves to #141824, which is the only surface that actually steps forward.
//
// The band across the top is the point of the screen. Before reading any trace
// you have to know whether the thing that drew it was switched on: on the L120
// tandem an idle extruder holds its last setpoint, so its diameter trace is a
// flat line at the target — indistinguishable from a flawless run.

const fmt = (v, unit = '', digits = 2) =>
  v == null || Number.isNaN(v) ? '—' : `${Number(v).toFixed(digits)}${unit}`;

const renderChart = (def, common) => {
  const { fields, title, unit } = def;
  if (def.kind === 'double') {
    return <MachineDoubleLineChart {...common} fields={fields} title={title} />;
  }
  if (def.kind === 'multi') {
    // `unit` matters: the multi-series chart defaults to °C, and a traverse's
    // pulse limits were being labelled in degrees Celsius.
    return <MachineTempZonesChart {...common} fields={fields} title={title} unit={unit} />;
  }
  return <MachinePercentageLineChart {...common} fields={fields} title={title} />;
};

/** A boolean field, as a chip that says how much of the drum it was on for. */
const StateChip = ({ label, state }) => {
  if (!state || state.kind !== 'state') return null;
  const pct = state.onPct;
  const bg = pct >= 95 ? 'success' : pct >= 5 ? 'warning' : 'secondary';
  return (
    <Badge
      bg={bg}
      variant="phoenix"
      title={
        state.flips
          ? `Switched ${state.flips} time${state.flips === 1 ? '' : 's'} during this drum`
          : 'Never switched during this drum'
      }
    >
      {label} {pct >= 99.5 ? 'on' : pct < 0.5 ? 'off' : `${pct}%`}
      {/* A gauge that went on and off eleven times is a different story from one
          that was on throughout, and the percentage alone hides it. */}
      {state.flips > 1 ? ` · ${state.flips}×` : ''}
    </Badge>
  );
};

/** Set vs measured, as the one line worth reading first on an extrusion run. */
const DeviationChip = ({ label, summary, setField, actField, gateField }) => {
  const set = summary?.[setField];
  const act = summary?.[actField];
  const gate = gateField ? summary?.[gateField] : null;

  if (!set || !act || set.kind !== 'number' || act.kind !== 'number') return null;
  // Without the gauge on, `act` is whatever the laser last held. Reporting a
  // deviation from it would be inventing a measurement.
  if (gate && gate.kind === 'state' && gate.onPct < 5) return null;
  if (!(set.mean > 0)) return null;

  const bias = act.mean - set.mean;
  const biasPct = (bias / set.mean) * 100;
  const tone = Math.abs(biasPct) > 2 ? 'text-danger' : Math.abs(biasPct) > 1 ? 'text-warning' : 'text-success';

  return (
    <Card className="h-100" style={{ minWidth: 200 }}>
      <Card.Body className="p-2">
      <div className="fs-10 text-700">{label}</div>
      <div className="fs-8">
        <span className="fw-bold">{fmt(act.mean, ' mm')}</span>
        <span className="text-700"> vs {fmt(set.mean, ' mm')} set</span>
      </div>
      <div className={`fs-10 ${tone}`}>
        {bias >= 0 ? '+' : ''}{fmt(bias, ' mm')} ({biasPct >= 0 ? '+' : ''}{fmt(biasPct, '%', 1)})
      </div>
      <div className="fs-10 text-700">
        ± {fmt(act.stdDev, ' mm', 3)} · {fmt(act.min, '', 2)}–{fmt(act.max, '', 2)}
      </div>
      {/* How much of the drum was actually under the laser. A wall measured for
          a third of the run is a different claim from one measured throughout,
          and averaging the two into one number hides which you are looking at. */}
      {act.excluded > 0 && (
        <div className="fs-10 text-warning">
          measured {Math.round((act.samples / (act.samples + act.excluded)) * 100)}% of the drum
        </div>
      )}
      </Card.Body>
    </Card>
  );
};

const TokenChartsPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    tokenId,
    token,
    task,
    phasePlan,
    machineId,
    startTime,
    endTime,
    bucket = 'machine_data',
    returnTo
  } = state || {};

  const goBack = () =>
    // `-1` rather than the path, so the project page keeps its scroll and its
    // open task table. The explicit path is the fallback for a fresh tab.
    returnTo ? navigate(returnTo) : navigate(-1);

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const fields = useMemo(() => SUMMARY_FIELDS[machineId] || [], [machineId]);
  const gates = useMemo(
    () =>
      Object.entries(SUMMARY_GATES[machineId] || {})
        .map(([measured, gate]) => `${measured}:${gate}`)
        .join(','),
    [machineId]
  );

  useEffect(() => {
    if (!machineId || !startTime || !fields.length) return undefined;
    let alive = true;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const { data } = await axios.get(
          `${INFLUX_API}/api/influx/${machineId}/run-summary`,
          { params: { start: startTime, end: endTime, bucket, fields: fields.join(','), gates } }
        );
        if (alive) setSummary(data?.fields || {});
      } catch (e) {
        // Held back rather than guessed at: with no summary the gated charts
        // stay hidden, because a trace from an idle gauge looks like a good run.
        if (alive) setErr(e?.response?.data?.error || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [machineId, startTime, endTime, bucket, fields, gates]);

  const defs = useMemo(() => chartsFor(machineId, summary), [machineId, summary]);
  const common = { dynamicBucket: false, bucket, machineName: machineId, startTime, endTime };

  const speed = summary?.LINE_SPEED_ACT || summary?.LineSpeed || summary?.LINE_SPEED || summary?.ACT_SPEED || summary?.ACT_RAMP_VALUE_PER;

  return (
    <div className="mb-9 mt-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h3 className="mb-1">
            Machine traces —{' '}
            <span style={{ fontFamily: 'monospace' }}>{tokenId || '—'}</span>
          </h3>
          {machineId && (
            <p className="text-700 mb-0 fs-9">
              one drum on {machineId}, over the window it was produced in
            </p>
          )}
        </div>
        {/* Deliberately loud. The close used to be the modal's own faint ×,
            which on the dark theme was a grey glyph on a grey bar. */}
        <Button variant="danger" onClick={goBack}>
          ✕ Close
        </Button>
      </div>

      <div>
        {!machineId ? (
          <div className="text-700">No machine found for this drum.</div>
        ) : (
          <>
            {/* Side by side from xl up, stacked below it: on a phone two
                columns of figures this dense are unreadable. */}
            <Row className="g-3 mb-3">
              <Col xs={12} xl={6}>
            {/* ── what the line was doing ── */}
            <Card className="h-100">
              <Card.Body className="py-3">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span className="fs-9 text-700">
                  {token?.startedAt ? new Date(token.startedAt).toLocaleString() : '—'}
                  {' → '}
                  {token?.completedAt ? new Date(token.completedAt).toLocaleString() : 'now'}
                </span>
                {token?.producedLength != null && (
                  <span className="fs-9 text-700">
                    · {Number(token.producedLength).toFixed(1)} m produced
                  </span>
                )}
                {speed?.kind === 'number' && (
                  <span className="fs-9 text-700">
                    · {fmt(speed.mean, ' m/min', 0)} average, peak {fmt(speed.max, '', 0)}
                  </span>
                )}
                {loading && <Spinner animation="border" size="sm" variant="primary" />}
              </div>

              {machineId === 'l120' && summary && (
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <StateChip label="Filler extruder" state={summary.E120_F_ON_OFF} />
                  <StateChip label="Filler gauge" state={summary.FILLING_DIA_ON_OFF} />
                  <StateChip label="Jacketing extruder" state={summary.E120_J_ON_OFF} />
                  <StateChip label="Jacketing gauge" state={summary.JACKETING_DIA_ON_OFF} />
                </div>
              )}
              {machineId === 'skip2250' && summary && (
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <StateChip label="Taping head" state={summary.TTH1_ON_OFF} />
                </div>
              )}

              <Row className="g-2">
                <Col xs="auto">
                  <DeviationChip
                    label="Filler wall"
                    summary={summary}
                    setField="FILLING_SET_DIA"
                    actField="FILLING_DIA_ACT_VALUE"
                    gateField="FILLING_DIA_ON_OFF"
                  />
                </Col>
                <Col xs="auto">
                  <DeviationChip
                    label="Jacketing wall"
                    summary={summary}
                    setField="JACKETING_SET_DIA"
                    actField="JACKETING_DIA_ACT_VALUE"
                    gateField="JACKETING_DIA_ON_OFF"
                  />
                </Col>
                <Col xs="auto">
                  <DeviationChip
                    label="Wall"
                    summary={summary}
                    setField="DiameterSetValue"
                    actField="ActualDiameter"
                  />
                </Col>
              </Row>

              {err && (
                <div className="text-warning fs-10 mt-2">
                  Could not read the run summary ({err}) — traces that depend on knowing
                  which gauge was on are held back.
                </div>
              )}
              </Card.Body>
            </Card>
              </Col>

              <Col xs={12} xl={6}>
                {/* ── what it was supposed to be ── */}
                <TokenPhaseSpec
                  phasePlan={phasePlan}
                  task={task}
                  token={token}
                  machineId={machineId}
                  summary={summary}
                />
              </Col>
            </Row>

            {/* ── the traces ── */}
            {!defs.length ? (
              <Card>
                <Card.Body className="py-4 text-center text-700">
                  {loading
                    ? 'Reading what the line was doing…'
                    : `Nothing to plot for ${machineId} over this window.`}
                </Card.Body>
              </Card>
            ) : (
              <Row className="g-3">
                {defs.map(def => (
                  <Col xs={12} xl={def.kind === 'multi' ? 12 : 6} key={def.key}>
                    <Card className="h-100">
                      <Card.Body className="p-2">
                        <LazyLoadChart>{renderChart(def, common)}</LazyLoadChart>
                        {def.note && (
                          <div className="text-700 fs-10 px-2 pb-1">{def.note}</div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TokenChartsPage;
